<script setup lang="ts">
import type { PrimitiveProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'

import { reactiveOmit } from '@vueuse/core'
import { cn } from '~ds/lib/utils'
import { Primitive } from 'reka-ui'

import type { BadgeVariants } from '.'

import { badgeVariants } from '.'
import BadgeDot from './BadgeDot.vue'

// `withDefaults`, not the recipe's defaultVariants: Vue casts an absent boolean prop to `false`,
// so a `uppercase` default that lives only in cva never applies to a call site that omits it.
const props = withDefaults(defineProps<PrimitiveProps & {
  variant?: BadgeVariants['variant'];
  tone?: BadgeVariants['tone'];
  // Literal `boolean`, not BadgeVariants['dot']: the SFC compiler cannot resolve the
  // VariantProps<> generic to Boolean, so a bare `dot` attribute would reach cva as '' (falsy)
  // instead of true and the dot would silently not render. The literal keeps Vue's ''-to-true
  // coercion.
  dot?: boolean;
  // The pill corner, restored at design's request; the toolkit tag corner stays the default.
  rounded?: boolean;
  // Uppercase is the default presentation design asked for; pass `:uppercase="false"` to opt out.
  uppercase?: boolean;
  class?: HTMLAttributes['class'];
}>(), { dot: false, rounded: false, uppercase: true })

const delegatedProps = reactiveOmit(props, 'class', 'dot', 'rounded', 'uppercase')
</script>

<template>
  <Primitive
    data-slot="badge"
    :class="cn(badgeVariants({ variant, tone, rounded, dot, uppercase }), props.class)"
    v-bind="delegatedProps"
  >
    <!-- The toolkit's leading dot. `BadgeDot` stays exported for hand-composed cases; here it is
         the `dot` axis, so the canonical Figma set can publish it as a variant. -->
    <BadgeDot v-if="dot" />
    <slot />
  </Primitive>
</template>
